import { Route } from '@angular/router';
import { initialDataResolver } from './app.resolvers';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { TenantGuard } from './core/auth/guards/tenant.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { AccesoDenegadoComponent } from './modules/auth/acceso-denegado/acceso-denegado.component';
import { permissionGuard } from '@hta/auth';
import { DevModeGuard } from './core/auth/guards/dev-mode.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    {path: '', pathMatch : 'full', redirectTo: 'example'},

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'example'},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')},
            {path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes')}
        ]
    },

    // Public invitation acceptance (no auth required initially, will redirect to login if needed)
    {
        path: 'invitation/accept',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: '', redirectTo: '/home', pathMatch: 'full'},
            {path: '', loadChildren: () => import('app/modules/auth/accept-invitation/accept-invitation.routes')}
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
            {path: 'tenant-selection', loadChildren: () => import('app/modules/auth/tenant-selection/tenant-selection.routes')},
            {path: 'create-tenant', loadChildren: () => import('app/modules/auth/create-tenant/create-tenant.routes')},
            {path: 'dev-debug', canActivate: [DevModeGuard], loadChildren: () => import('app/modules/auth/dev-debug/dev-debug.routes')},
          //  {path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')}
        ]
    },

    // Soporte routes (AuthGuard + permission check)
    {
        path: 'soporte',
        canActivate: [AuthGuard],
        component: LayoutComponent,
        data: { layout: 'futuristic' },
        children: [
            {
                path: 'activar',
                canActivate: [permissionGuard],
                data: {
                    permissions: 'corporativo.soporte.sesion',
                    redirectTo: '/acceso-denegado'
                },
                loadChildren: () => import('libs/shared-ui/src/lib/soporte/soporte-activar.routes')
            },
            {path: '', redirectTo: 'activar', pathMatch: 'full'},
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes')},
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard, TenantGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'acceso-denegado', component: AccesoDenegadoComponent},
            {path: 'example', loadChildren: () => import('app/modules/admin/example/example.routes')},
            {path: 'perfil', loadChildren: () => import('libs/shared-ui/src/lib/account-management/account-management.routes')},
             {path: 'corporativo', loadChildren: () => import('libs/shared-ui/src/lib/corporativo/corporativo.routes')},
             {path: 'colaboradores', loadChildren: () => import('libs/shared-ui/src/lib/colaboradores/colaboradores.routes')},
             {path: 'permisos', loadChildren: () => import('libs/shared-ui/src/lib/permisos/permisos.routes')},
             {path: 'servicios', loadChildren: () => import('libs/shared-ui/src/lib/servicios-contratados/dashboard-servicios/servicios-contratados.routes')},
            {path: 'facturas', loadChildren: () => import('libs/shared-ui/src/lib/facturas-pagos/facturas.routes')},
            {path: 'productos', loadChildren: () => import('libs/shared-ui/src/lib/list-productos/list-productos.routes')},
            {path: 'clientes', loadChildren: () => import('libs/shared-ui/src/lib/clientes/clientes.routes')},
            {path: 'consultaFacturas', loadChildren: () => import('libs/shared-ui/src/lib/consulta-cfdis/consulta-cfdis.routes')},
            {
                path: 'facturas-pagos',
                canActivate: [permissionGuard],
                data: {
                    permissions: 'corporativo.facturacion.ver',
                    redirectTo: '/acceso-denegado'
                },
                loadChildren: () => import('libs/shared-ui/src/lib/facturas-pagos/facturas.routes')
            },
            {
                path: 'admin/precios-suscripcion',
                canActivate: [permissionGuard],
                data: {
                    permissions: 'corporativo.suscripcion.precios.gestionar',
                    redirectTo: '/acceso-denegado'
                },
                loadComponent: () => import('app/modules/admin/precios-suscripcion/precios-suscripcion.component').then(m => m.PreciosSuscripcionComponent)
            },
            {
                path: 'facturacion',
                canActivate: [permissionGuard],
                data: {
                    permissions: 'corporativo.facturacion.ver',
                    redirectTo: '/acceso-denegado'
                },
                loadChildren: () => import('app/modules/facturacion/facturacion.routes')
            },
        ]
    }
];
